import { Checkbox } from '@1trading/react/components/ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { ManagementRole } from '@/constants/institution';
import { useInstitutionDetailsV2Answers } from '@/features/institution/hooks/useInstitutionDetails';
import type { InstitutionTaxSelfCertificationOrDefaults } from '@/features/institution/tax-self-certification/schema';

const SOMEONE_ELSE = 'SOMEONE_ELSE';

const SIGNATORY_ROLES: ManagementRole[] = [ManagementRole.DIRECTOR, ManagementRole.AUTHORISED_SIGNATORY];

const DECLARATIONS = ['accurate', 'consentToReporting', 'undertakeToNotifyChanges'] as const;

const InstitutionTaxSelfCertificationFieldsetDeclarationLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.taxSelfCertification.declaration.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.taxSelfCertification.declaration.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionTaxSelfCertificationFieldsetDeclarationLegend.displayName = 'InstitutionTaxSelfCertificationFieldsetDeclarationLegend';

const InstitutionTaxSelfCertificationFieldsetDeclaration = () => {
  const form = useFormContext<InstitutionTaxSelfCertificationOrDefaults>();
  const { data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { t } = useTranslation();
  const signatoryPartyId = form.watch('signatory.partyId');
  const signatories = (institutionDetailsAnswers?.ownership?.management ?? []).filter(({ roles }) => roles.some((role) => SIGNATORY_ROLES.includes(role)));
  const isListedSignatory = signatories.some(({ partyId }) => partyId === signatoryPartyId);
  const signatoryOption = isListedSignatory ? signatoryPartyId : signatoryPartyId && SOMEONE_ELSE;

  // A signatory entered as someone else gets a new partyId, so they aren't mistaken for another person in the application
  const selectSignatory = (value: string) => {
    const person = signatories.find(({ partyId }) => partyId === value);

    form.setValue('signatory.partyId', person ? person.partyId : crypto.randomUUID(), { shouldValidate: true });
    form.setValue('signatory.fullName', person ? `${person.firstName} ${person.lastName}` : '');
  };

  return (
    <div className="space-y-6">
      {DECLARATIONS.map((declaration) => (
        <FormField
          key={declaration}
          control={form.control}
          name={`declaration.${declaration}`}
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-row items-center gap-4">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <FormLabel>{t(`institutionDetails.taxSelfCertification.declaration.${declaration}`)}</FormLabel>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
      <FormField
        control={form.control}
        name="signatory.partyId"
        render={() => (
          <FormItem>
            <FormLabel>{t('institutionDetails.taxSelfCertification.declaration.signatory')}*</FormLabel>
            <FormControl>
              <RadioGroup value={signatoryOption} variant="cards" aria-label={t('institutionDetails.taxSelfCertification.declaration.signatory')} onValueChange={selectSignatory}>
                {signatories.map((person) => (
                  <FormItem key={person.partyId}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={person.partyId} />
                      </FormControl>
                      <FormLabel contained>
                        {person.firstName} {person.lastName}
                      </FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
                <FormItem>
                  <RadioGroupItemCard variant="outline">
                    <FormControl>
                      <RadioGroupItem value={SOMEONE_ELSE} />
                    </FormControl>
                    <FormLabel contained>{t('institutionDetails.taxSelfCertification.declaration.someoneElse')}</FormLabel>
                  </RadioGroupItemCard>
                </FormItem>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {signatoryOption === SOMEONE_ELSE ? (
        <FormField
          control={form.control}
          name="signatory.fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.taxSelfCertification.declaration.signatoryFullName')}*</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t('institutionDetails.taxSelfCertification.placeholders.signatoryFullName')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
      <FormField
        control={form.control}
        name="signatory.capacity"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.taxSelfCertification.declaration.signatoryCapacity')}*</FormLabel>
            <FormControl>
              <Input {...field} placeholder={t('institutionDetails.taxSelfCertification.placeholders.signatoryCapacity')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
InstitutionTaxSelfCertificationFieldsetDeclaration.displayName = 'InstitutionTaxSelfCertificationFieldsetDeclaration';

export { InstitutionTaxSelfCertificationFieldsetDeclaration, InstitutionTaxSelfCertificationFieldsetDeclarationLegend };
